// React
import { useCallback, useMemo, useRef, useState } from 'react';

// Form seam (GRAIN-2551 shared TanStack + Zod primitives)
import { Form } from '@components/Form';
import { useFormContext, type FormApi } from '@components/Form/FormProvider';
import { shouldShowFieldError, useFieldErrorVisibility } from '@components/Form/fieldErrorVisibility';

// UI primitives
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@/components/ui/tooltip';
import { Field, FieldLabel, FieldContent, FieldError } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

// Icons
import { infoIcon } from './InfoIcon';

// Components
import { CustomerEmailCombobox } from './CustomerEmailCombobox';
import { CreateOrganisationCombobox } from './CreateOrganisationCombobox';
import { BillingAddressCombobox } from './BillingAddressCombobox';
import { SourceCombobox } from './SourceCombobox';
import { SalespersonSelector } from './SalespersonSelector';
import { SubmitButton } from './SubmitButton';

// Hooks and utilities
import { savedBillingAddressId } from './billingAddressFallback';
import { useCreateOrderContext } from './CreateOrderContext';
import { useCreateOrder } from '@hooks/orders';
import { useUpdateContact } from '@hooks/contacts';
import { useServerErrors } from '@hooks/useServerErrors';
import { useTranslation } from '@hooks/useTranslation';
import { toast } from '@/hooks/use-toast';
import { getFieldErrors } from '@api/client';
import { usePostHog } from '@posthog/react';
import {
  makeCreateOrderSchema,
  isSalespersonFieldInvalid,
  type CreateOrderFormData,
} from '@/react/schemas/order.schema';
import {
  ORDER_SERVER_ERROR_TARGETS,
  ORDER_BASE_ERROR_TARGETS,
  type OrderServerErrorTarget,
} from '../../../EditOrderDetails/serverErrorTargets';
import type { Salesperson, Order } from '@api/orders';
import type { ApiContact } from '@api/contacts';

import { FIELD_SURFACE, FIELD_SURFACE_BORDERED } from "@/components/ui/field-surface";
import { cn } from "@/lib/utils";
interface CreateOrderFormProps {
  salespersons: Salesperson[];
  defaultSalespersonId?: number;
  onSuccess: (order: Order) => void;
}

const PHONE_PREFIX_RE = /^\+65\s*/;

const labelClass = 'font-inter text-sm font-semibold text-grey leading-normal';
const optionalClass = 'font-inter text-sm font-normal text-grey leading-normal';
const inputClass =
  cn(FIELD_SURFACE_BORDERED, 'font-inter h-[45px] w-full px-6 py-4 text-sm text-off-black placeholder:text-grey-light focus-visible:outline-none focus-visible:ring-0');

function stripPhonePrefix(value: string | null): string {
  return (value ?? '').replace(PHONE_PREFIX_RE, '');
}

export function CreateOrderForm({
  salespersons,
  defaultSalespersonId,
  onSuccess,
}: CreateOrderFormProps) {
  const posthog = usePostHog();
  const { state } = useCreateOrderContext();
  const { t } = useTranslation();

  const createOrder = useCreateOrder();
  const updateContact = useUpdateContact();

  const schema = useMemo(
    () =>
      makeCreateOrderSchema({
        contactRequired: t('create_order.validation.contact_required'),
        sourceRequired: t('create_order.validation.source_required'),
        salespersonRequired: t('create_order.validation.salesperson_required'),
      }),
    [t],
  );

  // A signed-in user who isn't on the sales team must not be seeded into the
  // picker: the picker wouldn't list them, so the field would read as empty while
  // holding an id the server rejects. Treat that as no selection.
  const seededSalespersonId = state.orderDraft.salespersonId ?? defaultSalespersonId ?? 0;
  const eligibleDefaultSalespersonId = salespersons.some((sp) => sp.id === seededSalespersonId)
    ? seededSalespersonId
    : 0;

  // Seeded once at mount; the panel remounts on each sub-view switch and re-seeds
  // from context, so mirrored edits and sub-view results survive navigation.
  const defaultValues = useMemo<CreateOrderFormData>(
    () => ({
      contact_id: state.selectedContact?.id ?? 0,
      contact_email: state.selectedContact?.email ?? '',
      contact_name: state.orderDraft.customerName,
      contact_phone: state.orderDraft.phoneNumber,
      customer_notes: state.orderDraft.customerNote,
      organisation_id: state.orderDraft.organisationId,
      organisation_name: state.orderDraft.organisation,
      order_source_id: state.selectedSourceId,
      is_outbound: state.orderDraft.orderType === 'outbound',
      salesperson_id: eligibleDefaultSalespersonId,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const formRef = useRef<FormApi | null>(null);
  const [submitError, setSubmitError] = useState<Error | null>(null);
  useServerErrors<OrderServerErrorTarget>({
    error: submitError,
    formRef,
    mapServerFieldToTargets: ORDER_SERVER_ERROR_TARGETS,
    baseErrorTargets: ORDER_BASE_ERROR_TARGETS,
  });

  const isPending = createOrder.isPending || updateContact.isPending || state.isBillingAddressLoading;

  const handleSubmit = useCallback(
    async (values: CreateOrderFormData) => {
      if (state.isBillingAddressLoading) return;
      setSubmitError(null);

      const contact = state.selectedContact;
      if (!contact) return;
      // Eligibility can't live in the schema (needs the salespersons list);
      // the field is already red, so bail before mutating.
      if (!salespersons.some((sp) => sp.id === values.salesperson_id)) return;

      // Billing is context-driven (async-hydrated), not a form value.
      const selectedBillingAddress = state.selectedBillingAddress;

      try {
        const nameParts = values.contact_name.trim().split(/\s+/);
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        // GRAIN-2485: org is an ORDER property (sent as organisation_id), never
        // written back to the contact. Billing is the same: it belongs to the
        // order, so writing the picked address back here would change the
        // billed party on every other order for this customer.
        const hasContactChanges =
          values.contact_name.trim() !== (contact.full_name ?? '') ||
          values.contact_phone.trim() !== stripPhonePrefix(contact.mobile_number) ||
          values.customer_notes.trim() !== (contact.notes ?? '');

        if (hasContactChanges) {
          await updateContact.mutateAsync({
            id: contact.id,
            payload: {
              first_name: firstName,
              last_name: lastName,
              mobile_number: values.contact_phone.trim() || undefined,
              notes: values.customer_notes.trim() || undefined,
            },
          });
        }

        const order = await createOrder.mutateAsync({
          contact_id: contact.id,
          organisation_id: values.organisation_id ?? null,
          order_source_id: values.order_source_id ? Number(values.order_source_id) : undefined,
          is_outbound: values.is_outbound,
          salesperson_id: values.salesperson_id,
          selected_billing_address_id: savedBillingAddressId(selectedBillingAddress?.id),
        });
        posthog.capture('order_created', {
          order_id: order.id,
          order_type: values.is_outbound ? 'outbound' : 'inbound',
          order_source_id: values.order_source_id ? Number(values.order_source_id) : null,
        });
        onSuccess(order);
      } catch (err: unknown) {
        setSubmitError(err as Error);
        const fieldErrors = getFieldErrors(err);
        const mappedToField = fieldErrors
          ? Object.keys(fieldErrors).some((key) => ORDER_SERVER_ERROR_TARGETS[key])
          : false;
        if (!mappedToField) {
          toast({
            description: err instanceof Error ? err.message : t('create_order.errors.create_failed'),
            variant: 'destructive',
          });
        }
      }
    },
    [state, salespersons, createOrder, updateContact, posthog, onSuccess, t],
  );

  return (
    <Form<CreateOrderFormData>
      schema={schema}
      defaultValues={defaultValues}
      formRef={formRef}
      onSubmit={handleSubmit}
      isPending={isPending}
      className="flex min-h-0 flex-1 flex-col overflow-x-hidden font-inter antialiased"
    >
      <CreateOrderFields
        salespersons={salespersons}
        isPending={isPending}
      />
    </Form>
  );
}

interface CreateOrderFieldsProps {
  salespersons: Salesperson[];
  isPending: boolean;
}

function CreateOrderFields({ salespersons, isPending }: CreateOrderFieldsProps) {
  const { form } = useFormContext();
  const { state, actions } = useCreateOrderContext();
  const { t } = useTranslation();
  const submitted = useFieldErrorVisibility();

  const hasContact = state.selectedContact !== null;
  const disabled = !hasContact || isPending;

  const selectedBillingAddress = state.selectedBillingAddress;
  const selectedPostalCode = selectedBillingAddress?.postal_code ?? '';

  const handleContactSelect = useCallback(
    (contact: ApiContact) => {
      // Context selection also kicks off the async billing fetch; mirror the
      // contact fields into the form so validation and submit values track.
      actions.setSelectedContact(contact);
      form.setFieldValue('contact_id', contact.id);
      form.setFieldValue('contact_email', contact.email);
      form.setFieldValue('contact_name', contact.full_name ?? '');
      form.setFieldValue('contact_phone', stripPhonePrefix(contact.mobile_number));
      form.setFieldValue('customer_notes', contact.notes ?? '');
      form.setFieldValue('organisation_id', contact.organisation?.id ?? null);
      form.setFieldValue('organisation_name', contact.organisation?.name ?? '');
    },
    [actions, form],
  );

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col gap-8 overflow-y-auto px-8 pb-8 pt-6">
        <div className="grid grid-cols-2 gap-8">
          <form.Field name="contact_id">
            {(field) => {
              const isInvalid = shouldShowFieldError(field.state.meta, submitted);
              return (
                <Field>
                  <FieldLabel className={labelClass}>{t('create_order.labels.customer_email')}</FieldLabel>
                  <FieldContent>
                    <CustomerEmailCombobox
                      selectedContact={state.selectedContact}
                      onSelect={handleContactSelect}
                      onCreateNew={() => actions.setView('create-customer')}
                      invalid={isInvalid}
                    />
                    {isInvalid && <FieldError errors={field.state.meta.errors} />}
                  </FieldContent>
                </Field>
              );
            }}
          </form.Field>

          <form.Field name="contact_name">
            {(field) => (
              <Field>
                <FieldLabel className={labelClass}>{t('create_order.labels.customer_name')}</FieldLabel>
                <FieldContent>
                  <Input
                    value={(field.state.value as string) || ''}
                    onChange={(e) => {
                      field.handleChange(e.target.value);
                      actions.updateOrderDraft({ customerName: e.target.value });
                    }}
                    onBlur={field.handleBlur}
                    placeholder={t('create_order.placeholders.name')}
                    disabled={disabled}
                    autoComplete="off"
                    className={inputClass}
                  />
                </FieldContent>
              </Field>
            )}
          </form.Field>
        </div>

        <div className="grid grid-cols-2 gap-8">
          <form.Field name="contact_phone">
            {(field) => (
              <Field>
                <FieldLabel className={labelClass}>{t('create_order.labels.phone_number')}</FieldLabel>
                <FieldContent>
                  <Input
                    type="tel"
                    value={(field.state.value as string) || ''}
                    onChange={(e) => {
                      field.handleChange(e.target.value);
                      actions.updateOrderDraft({ phoneNumber: e.target.value });
                    }}
                    onBlur={field.handleBlur}
                    placeholder={t('create_order.placeholders.phone')}
                    disabled={disabled}
                    autoComplete="off"
                    className={inputClass}
                  />
                </FieldContent>
              </Field>
            )}
          </form.Field>

          <form.Field name="organisation_id">
            {() => (
              <Field>
                <div className="flex items-center justify-between">
                  <FieldLabel className={labelClass}>{t('create_order.labels.organisation')}</FieldLabel>
                  <span className={optionalClass}>{t('create_order.labels.optional')}</span>
                </div>
                <FieldContent>
                  <CreateOrganisationCombobox
                    selected={
                      state.orderDraft.organisationId !== null
                        ? { id: state.orderDraft.organisationId, name: state.orderDraft.organisation }
                        : null
                    }
                    onSelect={(org) => {
                      form.setFieldValue('organisation_id', org.id);
                      form.setFieldValue('organisation_name', org.name);
                      actions.updateOrderDraft({ organisationId: org.id, organisation: org.name });
                    }}
                    onClear={() => {
                      form.setFieldValue('organisation_id', null);
                      form.setFieldValue('organisation_name', '');
                      actions.updateOrderDraft({ organisationId: null, organisation: '' });
                    }}
                    disabled={disabled}
                  />
                </FieldContent>
              </Field>
            )}
          </form.Field>
        </div>

        <div className="grid grid-cols-2 gap-8">
          <Field>
            <FieldLabel className={labelClass}>{t('create_order.labels.billing_address')}</FieldLabel>
            <FieldContent>
              <BillingAddressCombobox
                billingAddresses={state.orderDraft.billingAddresses}
                selectedId={state.orderDraft.selectedBillingAddressId}
                onSelect={(billingAddress) =>
                  actions.updateOrderDraft({
                    selectedBillingAddressId: billingAddress.id,
                    billingAddress: billingAddress.address,
                    postalCode: billingAddress.postal_code,
                  })
                }
                onCreateNew={hasContact ? () => actions.setView('create-billing-address') : undefined}
                disabled={disabled || state.isBillingAddressLoading}
              />
            </FieldContent>
          </Field>

          <Field>
            <FieldLabel className={labelClass}>{t('create_order.labels.postal_code')}</FieldLabel>
            <FieldContent>
              <Input
                value={selectedPostalCode}
                placeholder={t('create_order.placeholders.postal_code')}
                disabled={!hasContact || !selectedBillingAddress || isPending || state.isBillingAddressLoading}
                readOnly
                aria-readonly="true"
                autoComplete="off"
                className={inputClass}
              />
            </FieldContent>
          </Field>
        </div>

        <form.Field name="customer_notes">
          {(field) => (
            <Field>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FieldLabel className={labelClass}>{t('create_order.labels.customer_note')}</FieldLabel>
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button type="button" aria-label="More info" className="text-grey">
                          {infoIcon}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>{t('create_order.labels.tooltip_internal')}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <span className={optionalClass}>{t('create_order.labels.optional')}</span>
              </div>
              <FieldContent>
                <Textarea
                  value={(field.state.value as string) || ''}
                  onChange={(e) => {
                    field.handleChange(e.target.value);
                    actions.updateOrderDraft({ customerNote: e.target.value });
                  }}
                  onBlur={field.handleBlur}
                  placeholder={t('create_order.placeholders.customer_note')}
                  disabled={disabled}
                  className={cn(FIELD_SURFACE, "font-inter h-[84px] resize-none px-6 py-4 text-sm text-off-black placeholder:text-grey-light focus-visible:ring-0")}
                />
              </FieldContent>
            </Field>
          )}
        </form.Field>

        <div className="grid grid-cols-2 gap-8">
          <form.Field name="order_source_id">
            {(field) => {
              const isInvalid = shouldShowFieldError(field.state.meta, submitted);
              return (
                <Field>
                  <FieldLabel className={labelClass}>{t('create_order.labels.source')}</FieldLabel>
                  <FieldContent>
                    <SourceCombobox
                      sources={state.orderSources}
                      selectedId={(field.state.value as string) || ''}
                      onSelect={(id) => {
                        field.handleChange(id);
                        actions.setSelectedSourceId(id);
                      }}
                      onCreateNew={() => actions.setView('create-source')}
                      invalid={isInvalid}
                    />
                    {isInvalid && <FieldError errors={field.state.meta.errors} />}
                  </FieldContent>
                </Field>
              );
            }}
          </form.Field>

          <form.Field name="is_outbound">
            {(field) => (
              <Field>
                <FieldLabel className={labelClass}>{t('create_order.labels.order_type')}</FieldLabel>
                <FieldContent>
                  <Select
                    value={field.state.value ? 'outbound' : 'inbound'}
                    onValueChange={(value) => {
                      field.handleChange(value === 'outbound');
                      actions.updateOrderDraft({ orderType: value });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="inbound">{t('create_order.order_types.inbound')}</SelectItem>
                      <SelectItem value="outbound">{t('create_order.order_types.outbound')}</SelectItem>
                    </SelectContent>
                  </Select>
                </FieldContent>
              </Field>
            )}
          </form.Field>
        </div>
      </div>

      <div className="flex shrink-0 items-center justify-between border-t border-dinnerplate-white px-8 py-6">
        <form.Field name="salesperson_id">
          {(field) => {
            const value = (field.state.value as number) || 0;
            const showError = shouldShowFieldError(field.state.meta, submitted);
            const invalid = isSalespersonFieldInvalid(field.state.meta, value, submitted, salespersons);
            return (
              <div className="flex flex-col gap-1">
                <SalespersonSelector
                  salespersons={salespersons}
                  selectedId={value || undefined}
                  onSelect={(id) => {
                    field.handleChange(id);
                    actions.updateOrderDraft({ salespersonId: id });
                  }}
                  invalid={invalid}
                />
                {/* max-w matches the trigger's width so a long error wraps instead of stretching it; min-h reserves space so a short one doesn't shift the footer. */}
                <div className="min-h-7 max-w-[212px]">
                  {showError && <FieldError errors={field.state.meta.errors} />}
                </div>
              </div>
            );
          }}
        </form.Field>
        <SubmitButton label={t('create_order.actions.create')} />
      </div>
    </>
  );
}
